import { Platform } from 'react-native';

export const BREAKPOINTS = {
  DESKTOP: 1025,
  LARGE_DESKTOP: 1440,
} as const;

/** Shared spacing scale (React Native logical pixels). */
export const spacing = {
  xs: 4,
  /** Tight inline gap (e.g. icon to label). */
  small: 5,
  sm: 8,
  md: 16,
} as const;

/** Shared border radii (CSS px on web). */
export const borderRadius = {
  /** Large rounded corners (e.g. welcome promo video). */
  lg: 14,
} as const;

/** Shared font sizes for cross-cutting UI (labels, inline icons). */
export const typography = {
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
  },
} as const;

export const EMME_FONT_FAMILY = Platform.select({
  ios: 'Helvetica Neue',
  android: 'sans-serif',
  web: 'Helvetica Neue, Helvetica, Arial, sans-serif',
});

/** Welcome / marketing home: Neue only on web (no classic Helvetica fallback). */
export const WELCOME_SANS_FONT_FAMILY = Platform.select({
  ios: 'Helvetica Neue',
  android: 'sans-serif',
  web: 'Helvetica Neue, Arial, sans-serif',
});

const FONTS = {
  HELVETICA: EMME_FONT_FAMILY,
  LORA: Platform.select({
    ios: 'Lora',
    android: 'Lora',
    web: 'Lora, serif',
  }),
};

export const FONT_STYLES = {
  helvetica: { fontFamily: FONTS.HELVETICA },
  welcomeHelveticaNeue: { fontFamily: WELCOME_SANS_FONT_FAMILY },
  lora: { fontFamily: FONTS.LORA },
};

export enum ZIndex {
  BottomLayer = 1,
  MiddleLayer = 2,
  TopLayer = 3,
}

export const BOTTOM_TAB_BAR_HEIGHT = 42;
export const MOBILE_HEADER_HEIGHT = 62;

// Aspect ratio constants
export const ASPECT_RATIOS = {
  SQUARE: 1, // 1:1 for profile photos
  PORTRAIT: 4 / 5, // 4:5 aspect ratio (width:height) for portrait gallery images
  LANDSCAPE: 16 / 9, // 16:9 aspect ratio (width:height) for landscape gallery images
} as const;

export const GALLERY_CONSTANTS = {
  LANDSCAPE_FLEX_BASIS: '100%',
  PORTRAIT_FLEX_BASIS: '48%',
  PORTRAIT_ASPECT_RATIO: ASPECT_RATIOS.PORTRAIT,
  LANDSCAPE_ASPECT_RATIO: ASPECT_RATIOS.LANDSCAPE,
  COLUMN_GAP: '4%',
  ROW_GAP: 16,
  MAX_WIDTH: 335,
  MAX_PORTRAIT_IMAGES: 8,
  BORDER_RADIUS: 3.3,
} as const;

export const ONBOARDING_FORM_HEIGHT = 675;

export const WELCOME_SECTION_VERTICAL_BASE = 56;
export const WELCOME_SECTION_VERTICAL_RHYTHM_EXTRA = 24;
export const WELCOME_SECTION_VERTICAL_STYLISTS_CALLOUT = 140;
export const STYLISTS_CALLOUT_ASPECT_RATIO = 671 / 816;
/** Vertical padding (top and bottom) for the welcome “keep in touch” section at wide layout. */
export const WELCOME_KEEP_IN_TOUCH_SECTION_PADDING_Y = 100;

/** Vertical padding for the welcome “how emme works” section; scroll targets align with the title below `paddingTop`. */
export const WELCOME_HOW_EMME_WORKS_SECTION_PADDING_Y = 100;

export const WELCOME_SECTION_BREAKPOINT = 900;
export const WELCOME_SECTION_MAX_WIDTH = 1120;
export const WELCOME_CTA_MAX_WIDTH = 308;
export const WELCOME_PROMO_VIDEO_MAX_WIDTH = 400;
export const WELCOME_PROMO_VIDEO_URL =
  'https://res.cloudinary.com/dasev3v3c/video/upload/v1775580144/EMME_za8mjx.mp4';
export const WELCOME_SECTION_HORIZONTAL_BASE = 40;
export const WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA = 24;

export const WELCOME_SECTION_GAP_WIDE = 40;
export const WELCOME_SECTION_GAP_NARROW = 28;

export const WELCOME_SECTION_BODY_COPY = {
  fontSize: 16,
  letterSpacing: 0.9,
} as const;

export const WELCOME_TESTIMONIALS = {
  sectionVerticalPadding: 100,
  cardMaxWidth: 640,
  arrowCircleSize: 48,
  transitionMs: 240,
  autoAdvanceMs: 6000,
  sectionTitleMarginBottom: 32,
  navRowGap: 32,
  quoteMarginBottom: 20,
  quoteFontSize: 18,
  quoteLineHeight: 28,
  attributionFontSize: 15,
  attributionLineHeight: 22,
  arrowGlyphFontSize: 22,
  arrowGlyphLineHeight: 26,
  hitSlop: 12,
  sectionTitleLetterSpacing: 1,
} as const;

export const BOX_SHADOW_STYLE = {
  shadowColor: 'rgba(187, 181, 159, 0.35)',
  shadowOffset: {
    width: 0,
    height: 4,
  },
  shadowRadius: 24,
  shadowOpacity: 1,
  elevation: 2,
};
